import React from 'react';
import { MailIcon, MapPinIcon, PhoneIcon, SmartphoneIcon } from 'lucide-react';
import { PageHero } from '../components/ui/PageHero';
import { ReviewNote } from '../components/ui/ReviewNote';
import { EnquiryForm } from '../components/forms/EnquiryForm';
import { useSeo } from '../hooks/useSeo';
import { pageMeta } from '../data/seo';
import { contact, contactReviewNotes } from '../data/site';
import { heroImages } from '../data/images';
export function Contact() {
  useSeo(pageMeta.contact);
  return <>
      <PageHero title="Contact us" intro="Talk to the team about roadworks, civil engineering, water and sanitation or building projects in Botswana." image={heroImages.water} breadcrumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Contact'
    }]} data-id="element-658" data-mp="t2xdv6wrfs5e" />

      <section className="bg-charcoal py-16 md:py-24" data-id="element-659" data-mp="f9ef9kvld3i8">
        <div className="mx-auto grid max-w-site gap-14 px-5 md:px-8 lg:grid-cols-12 lg:gap-16" data-id="element-660" data-mp="f7v05xlo8yi1">
          <aside aria-labelledby="contact-details-title" className="lg:col-span-4" data-id="element-661" data-mp="a90gktfvr5wv">
            <h2 id="contact-details-title" className="font-display text-[32px] font-extrabold uppercase leading-none text-white" data-id="element-662" data-mp="15f6r44qfb3vg">
              Contact details
            </h2>
            <ul className="mt-8 divide-y divide-white/10 border-y border-white/10" data-id="element-663" data-mp="1e91wndvgiwbm">
              <ContactRow icon={PhoneIcon} label={contact.phone.label} data-id="element-664" data-mp="1j73i25gcv7q9">
                <a href={contact.phone.href} className="text-white hover:text-brand-light" data-id="element-665" data-mp="mgueg90azbv4">{contact.phone.display}</a>
              </ContactRow>
              <ContactRow icon={SmartphoneIcon} label={contact.mobile.label} data-id="element-666" data-mp="qixw5fdkfcou">
                <a href={contact.mobile.href} className="text-white hover:text-brand-light" data-id="element-667" data-mp="vgzkoftrg47p">{contact.mobile.display}</a>
              </ContactRow>
              <ContactRow icon={MailIcon} label="Email" data-id="element-668" data-mp="10e9ahwl2ahw2">
                <a href={`mailto:${contact.email}`} className="break-all text-white hover:text-brand-light" data-id="element-669" data-mp="26vcwunf5iv7w">{contact.email}</a>
              </ContactRow>
              <ContactRow icon={MapPinIcon} label="Office" data-id="element-670" data-mp="2bxbx3oflhc0m">
                <span className="text-white" data-id="element-671" data-mp="1m5sh5o8crwi1">{contact.physical}</span>
              </ContactRow>
              <ContactRow icon={MailIcon} label="Postal address" data-id="element-672" data-mp="1h7h7pelyzo0c">
                <span className="text-white" data-id="element-673" data-mp="1w62tz7qh71wo">{contact.postal}</span>
              </ContactRow>
            </ul>
            <div className="mt-6 space-y-3" data-id="element-674" data-mp="1r66rxe1c9kjr">
              <ReviewNote data-id="element-675" data-mp="1r4di5pvfik7j">{contactReviewNotes.email}</ReviewNote>
              <ReviewNote data-id="element-676" data-mp="13hx6weby90to">{contactReviewNotes.address}</ReviewNote>
            </div>
          </aside>

          <div className="lg:col-span-8" data-id="element-677" data-mp="yj3ukp8effv3">
            <section id="enquiry" aria-labelledby="enquiry-title" className="border-t-[3px] border-brand-red bg-charcoal-900 p-6 md:p-10" data-id="element-678" data-mp="yfy69c7lgi0f">
              <h2 id="enquiry-title" className="font-display text-[40px] font-extrabold uppercase leading-none text-white md:text-[48px]" data-id="element-679" data-mp="18foyf78omkxc">
                Request a quote
              </h2>
              <p className="mt-3 max-w-xl text-[16px] leading-relaxed text-steel-300" data-id="element-680" data-mp="frt4296l553q">
                Share the essentials of your project. The more detail you give on scope and location, the more useful our
                first conversation will be.
              </p>
              <div className="mt-8" data-id="element-681" data-mp="2hggxjnjkz0oo">
                <EnquiryForm data-id="element-682" data-mp="2hh8kk9z43q3c" />
              </div>
            </section>
          </div>
        </div>
      </section>
    </>;
}
function ContactRow({
  icon: Icon,
  label,
  children
}: {
  icon: React.ComponentType<{
    className?: string;
    'aria-hidden'?: boolean;
  }>;
  label: string;
  children: React.ReactNode;
}) {
  return <li className="flex gap-4 py-4" data-id="element-683" data-mp="3f3fy49de8sru">
      <Icon className="mt-1 h-5 w-5 shrink-0 text-brand-light" aria-hidden data-id="element-684" data-mp="314g6qtyhxzrz" />
      <div className="text-[16px]" data-id="element-685" data-mp="316iwrdznfswp">
        <span className="block text-[12px] font-semibold uppercase tracking-[0.14em] text-steel-400" data-id="element-686" data-mp="355i93jnpmry6">{label}</span>
        {children}
      </div>
    </li>;
}